/**
 * Skeleton placeholders rendered while classroom detail / list data is
 * still loading. Mirrors the actual layout shape (toolbar + grid) so the
 * incoming content swap doesn't cause a layout shift.
 */

const SkeletonBlock = ({
  className = ''
}: {
  className?: string;
}) => <div className={`animate-pulse rounded-sm bg-opacity-black-1 ${className}`} data-sentry-component="SkeletonBlock" data-sentry-source-file="ClassroomLoadingSkeletons.tsx" />;
function ContentToolbarSkeleton() {
  return <div className="mb-0 hidden items-center justify-between gap-md laptop-sm:flex" data-sentry-component="ContentToolbarSkeleton" data-sentry-source-file="ClassroomLoadingSkeletons.tsx">
      <div className="flex max-w-[800px] flex-1 basis-0 items-center">
        <SkeletonBlock className="h-[58px] w-full !rounded-sm" data-sentry-element="SkeletonBlock" data-sentry-source-file="ClassroomLoadingSkeletons.tsx" />
      </div>
      <div className="flex flex-1 basis-0 items-center justify-end gap-xs">
        <SkeletonBlock className="h-[28px] w-[48px]" data-sentry-element="SkeletonBlock" data-sentry-source-file="ClassroomLoadingSkeletons.tsx" />
        <SkeletonBlock className="h-[28px] w-[80px]" data-sentry-element="SkeletonBlock" data-sentry-source-file="ClassroomLoadingSkeletons.tsx" />
      </div>
    </div>;
}
function ContentCardSkeleton() {
  return <div className="flex min-h-[116px] min-w-[320px] items-stretch gap-sm bg-default px-2xs laptop-sm:min-h-[144px] laptop-sm:min-w-[600px] laptop-sm:gap-md laptop-sm:px-3xs laptop-lg:min-w-0" data-sentry-component="ContentCardSkeleton" data-sentry-source-file="ClassroomLoadingSkeletons.tsx">
      <div className="relative h-[52px] w-[79px] shrink-0 self-center laptop-sm:h-[90px] laptop-sm:w-[137px]">
        <SkeletonBlock className="h-full w-full !rounded-sm laptop-sm:!rounded-md" data-sentry-element="SkeletonBlock" data-sentry-source-file="ClassroomLoadingSkeletons.tsx" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-xs border-b border-alternative py-md laptop-sm:py-lg">
        <SkeletonBlock className="h-[20px] w-[120px] laptop-sm:h-[24px]" data-sentry-element="SkeletonBlock" data-sentry-source-file="ClassroomLoadingSkeletons.tsx" />
        <SkeletonBlock className="h-[24px] w-[60%]" data-sentry-element="SkeletonBlock" data-sentry-source-file="ClassroomLoadingSkeletons.tsx" />
      </div>
    </div>;
}
export function ClassroomContentListSkeleton({
  rows = 4
}: {
  rows?: number;
}) {
  return <div className="flex h-full min-h-0 flex-col gap-xs" data-sentry-component="ClassroomContentListSkeleton" data-sentry-source-file="ClassroomLoadingSkeletons.tsx">
      <ContentToolbarSkeleton data-sentry-element="ContentToolbarSkeleton" data-sentry-source-file="ClassroomLoadingSkeletons.tsx" />
      <div className="grid grid-cols-1 gap-x-md bg-default laptop-sm:grid-cols-[repeat(auto-fit,minmax(600px,1fr))]">
        {Array.from({
        length: rows
      }).map((_, i) => <ContentCardSkeleton key={i} />)}
      </div>
    </div>;
}
function StudentRowSkeleton() {
  return <div className="flex min-h-[144px] w-full min-w-0 items-stretch gap-none bg-default px-3xs laptop-sm:min-w-[600px] laptop-lg:min-w-0" data-sentry-component="StudentRowSkeleton" data-sentry-source-file="ClassroomLoadingSkeletons.tsx">
      <div className="flex min-w-0 flex-1 items-center gap-md border-b border-alternative py-lg pl-xs">
        <SkeletonBlock className="size-[62px] shrink-0 !rounded-full" data-sentry-element="SkeletonBlock" data-sentry-source-file="ClassroomLoadingSkeletons.tsx" />
        <div className="flex min-w-0 flex-1 flex-col gap-xs">
          <SkeletonBlock className="h-[24px] w-[40%]" data-sentry-element="SkeletonBlock" data-sentry-source-file="ClassroomLoadingSkeletons.tsx" />
          <SkeletonBlock className="h-[20px] w-[24%]" data-sentry-element="SkeletonBlock" data-sentry-source-file="ClassroomLoadingSkeletons.tsx" />
        </div>
      </div>
    </div>;
}
function ClassroomCardSkeleton() {
  return <div className="flex h-[164px] w-full flex-col gap-md overflow-hidden rounded-md border border-alternative bg-default p-lg" data-sentry-component="ClassroomCardSkeleton" data-sentry-source-file="ClassroomLoadingSkeletons.tsx">
      <div className="flex w-full items-start py-3xs">
        <SkeletonBlock className="size-[40px] !rounded-full" data-sentry-element="SkeletonBlock" data-sentry-source-file="ClassroomLoadingSkeletons.tsx" />
      </div>
      <div className="flex min-w-0 flex-col gap-3xs">
        <SkeletonBlock className="h-[24px] w-[60%]" data-sentry-element="SkeletonBlock" data-sentry-source-file="ClassroomLoadingSkeletons.tsx" />
        <SkeletonBlock className="h-[20px] w-[40%]" data-sentry-element="SkeletonBlock" data-sentry-source-file="ClassroomLoadingSkeletons.tsx" />
      </div>
    </div>;
}
export function ClassroomListGridSkeleton({
  cards = 8
}: {
  cards?: number;
} = {}) {
  return <div className="grid w-full grid-cols-1 justify-center gap-md mobile-lg:grid-cols-[repeat(auto-fit,minmax(400px,1fr))] mobile-lg:justify-start mobile-lg:gap-lg" data-sentry-component="ClassroomListGridSkeleton" data-sentry-source-file="ClassroomLoadingSkeletons.tsx">
      {Array.from({
      length: cards
    }).map((_, i) => <ClassroomCardSkeleton key={i} />)}
    </div>;
}
export function ClassroomStudentListSkeleton({
  rows = 4
}: {
  rows?: number;
} = {}) {
  return <div className="w-full min-w-0 bg-default laptop-sm:min-w-[600px]" data-sentry-component="ClassroomStudentListSkeleton" data-sentry-source-file="ClassroomLoadingSkeletons.tsx">
      <div className="mb-xs flex h-[58px] items-start justify-between gap-md bg-default">
        <div className="flex max-w-[800px] flex-1 basis-0 items-start">
          <SkeletonBlock className="h-[58px] w-full !rounded-sm" data-sentry-element="SkeletonBlock" data-sentry-source-file="ClassroomLoadingSkeletons.tsx" />
        </div>
        <div className="mt-[9px] flex min-w-0 flex-1 basis-0 justify-end gap-xs">
          <SkeletonBlock className="h-[28px] w-[48px]" data-sentry-element="SkeletonBlock" data-sentry-source-file="ClassroomLoadingSkeletons.tsx" />
          <SkeletonBlock className="h-[28px] w-[80px]" data-sentry-element="SkeletonBlock" data-sentry-source-file="ClassroomLoadingSkeletons.tsx" />
        </div>
      </div>
      <div className="grid grid-cols-1 items-start gap-x-md gap-y-none bg-default laptop-sm:grid-cols-[repeat(auto-fit,minmax(600px,1fr))]">
        {Array.from({
        length: rows
      }).map((_, i) => <StudentRowSkeleton key={i} />)}
      </div>
    </div>;
}